import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { createUsuario, updateUsuario, deleteUsuario } from "../services/dataService";

const ALL_PERMISOS = [
  { key: "crear", label: "Crear" },
  { key: "editar", label: "Editar" },
  { key: "eliminar", label: "Eliminar" },
  { key: "aprobar", label: "Aprobar" },
  { key: "ver_auditorias", label: "Ver Auditorías" },
  { key: "gestionar_stock", label: "Gestionar Stock" },
];

const PERMISOS_POR_ROL = {
  "Administrador": { crear: true, editar: true, eliminar: true, aprobar: true, ver_auditorias: true, gestionar_stock: true },
  "Calidad": { crear: true, editar: true, eliminar: false, aprobar: true, ver_auditorias: true, gestionar_stock: true },
  "Técnico": { crear: true, editar: true, eliminar: false, aprobar: false, ver_auditorias: false, gestionar_stock: false },
  "Dirección": { crear: false, editar: false, eliminar: false, aprobar: true, ver_auditorias: true, gestionar_stock: false },
};

export default function Usuarios({ usuarios = [], setUsuarios, registrarAuditoria, onNotify }) {
  const { puede } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: "", texto: "" });
  const [form, setForm] = useState({ nombre: "", email: "", password: "", rol: "Técnico", activo: true, permisos: { ...PERMISOS_POR_ROL["Técnico"] } });

  // Guardar en localStorage cuando cambian los usuarios (modo demo)
  useEffect(() => {
    if (setUsuarios && usuarios.length > 0) {
      localStorage.setItem('medtrack_usuarios', JSON.stringify(usuarios));
    }
  }, [usuarios, setUsuarios]);

  const handleCrear = async (e) => {
    e.preventDefault();
    if (!puede("crear")) {
      onNotify?.("No tenés permiso para crear usuarios", "error");
      return;
    }
    
    if (!form.nombre || !form.email || !form.password) {
      setMensaje({ tipo: "error", texto: "Todos los campos son obligatorios" });
      return;
    }
    if (usuarios.find(u => u.email.toLowerCase() === form.email.toLowerCase())) {
      setMensaje({ tipo: "error", texto: "El email ya está registrado" });
      return;
    }

    try {
      const saved = await createUsuario({
        nombre: form.nombre,
        email: form.email,
        password: form.password,
        rol: form.rol, 
        permisos: form.permisos,
        activo: true
      });

      if (setUsuarios) {
        setUsuarios(prev => [...prev, saved]);
      }
      
      registrarAuditoria?.(`Usuario "${saved.nombre}" creado (${saved.rol})`, "Usuarios");
      setMensaje({ tipo: "exito", texto: "Usuario creado correctamente" });
      setShowForm(false);
      setForm({ nombre: "", email: "", password: "", rol: "Técnico", activo: true, permisos: { ...PERMISOS_POR_ROL["Técnico"] } });
      onNotify?.("Usuario creado", "success");
      setTimeout(() => setMensaje({ tipo: "", texto: "" }), 3000);
    } catch (e) {
      setMensaje({ tipo: "error", texto: e.message });
    }
  };

  const toggleActivo = async (id) => {
    if (!puede("editar")) {
      onNotify?.("No tenés permiso para editar", "error");
      return;
    }
    const user = usuarios.find(u => u.id === id);
    if (!user) return;
    try {
      await updateUsuario({ id, activo: !user.activo });
      if (setUsuarios) {
        setUsuarios(prev => prev.map(u => u.id === id ? { ...u, activo: !user.activo } : u));
      }
      registrarAuditoria?.(`Usuario "${user.nombre}" ${user.activo ? "desactivado" : "activado"}`, "Usuarios");
      onNotify?.("Estado del usuario actualizado", "info");
    } catch (e) {
      onNotify?.(e.message, "error");
    }
  };

  const handleEliminar = async (id, nombre) => {
    if (!puede("eliminar")) {
      onNotify?.("Solo administradores pueden eliminar", "error");
      return;
    }
    if (window.confirm(`¿Eliminar usuario ${nombre}?`)) {
      try {
        await deleteUsuario(id);
        if (setUsuarios) {
          setUsuarios(prev => prev.filter(u => u.id !== id));
        }
        registrarAuditoria?.(`Usuario "${nombre}" eliminado`, "Usuarios");
        onNotify?.("Usuario eliminado", "info");
      } catch (e) {
        onNotify?.(e.message, "error");
      }
    }
  };

  return (
    <div style={{ padding: "32px", maxWidth: "1200px", margin: "0 auto", color: "#f8fafc" }}>
      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
        <h2 style={{ margin: 0, fontSize: "28px", fontWeight: "700", color: "#f8fafc" }}>Gestión de Usuarios</h2>
        {puede("crear") && (
          <button 
            onClick={() => setShowForm(true)}
            style={{
              padding: "10px 20px",
              background: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "background 0.2s"
            }}
            onMouseOver={(e) => e.target.style.background = "#2563eb"}
            onMouseOut={(e) => e.target.style.background = "#3b82f6"}
          >
            + Nuevo Usuario
          </button>
        )}
      </div>

      {/* MENSAJES */}
      {mensaje.texto && (
        <div style={{ 
          padding: "12px 16px", marginBottom: "20px", borderRadius: "8px", fontWeight: "500",
          background: mensaje.tipo === 'exito' ? '#064e3b' : '#450a0a',
          color: mensaje.tipo === 'exito' ? '#86efac' : '#fca5a5',
          border: `1px solid ${mensaje.tipo === 'exito' ? '#065f46' : '#7f1d1d'}`
        }}>
          {mensaje.texto}
        </div>
      )}

      {/* TABLA */}
      <div style={{ background: "#1e293b", borderRadius: "12px", overflow: "hidden", border: "1px solid #334155" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead style={{ background: "#0f172a" }}>
            <tr>
              {["NOMBRE", "EMAIL", "ROL", "ESTADO", "CREADO", "ACCIONES"].map((col, i) => (
                <th key={i} style={{ padding: "14px 16px", textAlign: "left", fontSize: "12px", fontWeight: "600", color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {usuarios.map((u, i) => (
              <tr key={u.id} style={{ borderBottom: "1px solid #334155", background: i % 2 === 0 ? "#1e293b" : "#162032" }}>
                <td style={{ padding: "14px 16px", color: "#f1f5f9" }}>{u.nombre}</td>
                <td style={{ padding: "14px 16px", color: "#cbd5e1" }}>{u.email}</td>
                <td style={{ padding: "14px 16px" }}>
                  <span style={{ padding: "4px 10px", borderRadius: "99px", fontSize: "12px", fontWeight: "600", background: u.rol === 'Administrador' ? '#7f1d1d' : u.rol === 'Calidad' ? '#1e40af' : '#14532d', color: "#f1f5f9" }}>
                    {u.rol}
                  </span>
                </td>
                <td style={{ padding: "14px 16px" }}>
                  <button 
                    onClick={() => toggleActivo(u.id)}
                    style={{ 
                      padding: "4px 12px", 
                      borderRadius: "6px", 
                      fontSize: "12px", 
                      fontWeight: "600",
                      cursor: "pointer",
                      border: "none",
                      background: u.activo ? "#16a34a" : "#dc2626",
                      color: "white"
                    }}
                  >
                    {u.activo ? "✅ Activo" : "❌ Inactivo"}
                  </button>
                </td>
                <td style={{ padding: "14px 16px", color: "#94a3b8" }}>{new Date(u.creado_en).toLocaleDateString('es-AR')}</td>
                <td style={{ padding: "14px 16px" }}>
                  <div className="flex gap-1">
                    {puede("eliminar") && (
                      <button 
                        onClick={() => handleEliminar(u.id, u.nombre)}
                        style={{
                          padding: "6px 12px",
                          background: "#dc2626",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          fontSize: "12px",
                          cursor: "pointer"
                        }}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {usuarios.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">
                  No hay usuarios registrados. Creá uno nuevo arriba ☝️
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL DARK MODE */}
      {showForm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999
        }} onClick={() => setShowForm(false)}>
          <div style={{
            background: "#1e293b", border: "1px solid #334155", borderRadius: "12px",
            padding: "28px", maxWidth: "450px", width: "90%", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
            position: "relative"
          }} onClick={e => e.stopPropagation()}>
            
            <button onClick={() => setShowForm(false)} style={{
              position: "absolute", top: "16px", right: "16px", background: "none", border: "none",
              color: "#94a3b8", fontSize: "22px", cursor: "pointer", lineHeight: 1
            }}>×</button>

            <h3 style={{ margin: "0 0 24px 0", fontSize: "20px", fontWeight: "700", color: "#f8fafc" }}>Crear Nuevo Usuario</h3>
            
            <form onSubmit={handleCrear} style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              {[
                { label: "Nombre Completo", type: "text", key: "nombre", placeholder: "Ej: Juan Pérez" },
                { label: "Email", type: "email", key: "email", placeholder: "juan@cardiohard.com" },
                { label: "Contraseña", type: "password", key: "password", placeholder: "••••••••" }
              ].map(field => (
                <div key={field.key}>
                  <label style={{ display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: "500", color: "#cbd5e1" }}>{field.label}</label>
                  <input 
                    type={field.type} required value={form[field.key]}
                    onChange={e => setForm({...form, [field.key]: e.target.value})}
                    placeholder={field.placeholder}
                    style={{
                      width: "100%", padding: "10px 12px", background: "#0f172a", border: "1px solid #334155",
                      borderRadius: "6px", color: "#f1f5f9", fontSize: "14px", outline: "none",
                      boxSizing: "border-box"
                    }}
                  />
                </div>
              ))}

              <div>
                <label style={{ display: "block", marginBottom: "6px", fontSize: "13px", fontWeight: "500", color: "#cbd5e1" }}>Rol</label>
                <select 
                  value={form.rol} onChange={e => setForm({...form, rol: e.target.value, permisos: { ...PERMISOS_POR_ROL[e.target.value] } })}
                  style={{
                    width: "100%", padding: "10px 12px", background: "#0f172a", border: "1px solid #334155",
                    borderRadius: "6px", color: "#f1f5f9", fontSize: "14px", outline: "none", boxSizing: "border-box"
                  }}
                >
                  <option value="Administrador">Administrador</option>
                  <option value="Calidad">Calidad</option>
                  <option value="Técnico">Técnico</option>
                  <option value="Dirección">Dirección</option>
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontSize: "13px", fontWeight: "500", color: "#cbd5e1" }}>Privilegios</label>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {ALL_PERMISOS.map(p => (
                    <label key={p.key} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#e2e8f0", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={form.permisos[p.key] || false}
                        onChange={e => setForm({ ...form, permisos: { ...form.permisos, [p.key]: e.target.checked } })}
                        style={{ accentColor: "#3b82f6", width: "16px", height: "16px", cursor: "pointer" }}
                      />
                      {p.label}
                    </label>
                  ))}
                </div>
                <p style={{ fontSize: "11px", color: "#64748b", margin: "6px 0 0" }}>Al cambiar el rol se restablecen los privilegios por defecto</p>
              </div>

              <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
                <button type="submit" style={{
                  flex: 1, padding: "10px", background: "#3b82f6", color: "white", border: "none",
                  borderRadius: "6px", fontSize: "14px", fontWeight: "600", cursor: "pointer"
                }}>Guardar</button>
                <button type="button" onClick={() => setShowForm(false)} style={{
                  flex: 1, padding: "10px", background: "transparent", color: "#cbd5e1",
                  border: "1px solid #475569", borderRadius: "6px", fontSize: "14px", fontWeight: "600", cursor: "pointer"
                }}>Cancelar</button>
              </div>
              <p style={{ fontSize: "11px", color: "#64748b", textAlign: "center", margin: "4px 0 0" }}>
                ⚠️ Modo demo: se guarda en localStorage
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}